import axios from 'axios'
import crypto from 'crypto'
import queryString from 'qs'

import { camelcaseKeys } from '@/axios/helpers/converters'

import configuration from '@/classes/Configuration'

const identityApi = axios.create({
  baseURL: configuration.get('identityApi'),
  transformResponse: [(data, headers) => {
    // format response from snakeCase to camelCase
    if (data && headers['content-type'].includes('application/json')) {
      return camelcaseKeys(JSON.parse(data), { deep: true })
    }
  }]
})

const base64URLEncode = (value) => {
  return value.toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

const sha256 = (buffer) => {
  return crypto.createHash('sha256').update(buffer).digest()
}

export default class AuthenticationService {
  /**
   * Get login redirect uri.
   *
   * @param {string} state
   *
   * @return {Promise}
   */
  static getLoginRedirectUri (state) {
    const verifier = base64URLEncode(crypto.randomBytes(64))
    const challenge = base64URLEncode(sha256(verifier))

    sessionStorage.setItem('verifier', verifier)

    return identityApi.getUri({
      url: `${configuration.get('identityApi')}${this.endpoint()}/authorize`,
      params: {
        'client_id': 'PortalCodePkce',
        'redirect_uri': configuration.get('portalUrl') + '/sign-in',
        'state': state,
        'response_type': 'code',
        'scope': 'identityApi openid profile portalApi offline_access',
        'code_challenge': challenge,
        'code_challenge_method': 'S256'
      }
    })
  }

  /**
   * Get logout redirect uri.
   *
   * @param {string} token
   *
   * @return {Promise}
   */
  static getLogoutRedirectUri (token) {
    return identityApi.getUri({
      url: `${configuration.get('identityApi')}${this.endpoint()}/endsession`,
      params: {
        'id_token_hint': token,
        'post_logout_redirect_uri': configuration.get('portalUrl') + '/sign-out'
      }
    })
  }

  /**
   * Login and get authorization token.
   *
   * @param {string} token
   *
   * @return {Promise}
   */
  static login (token) {
    var verifier = sessionStorage.getItem('verifier')

    return identityApi.post(`${this.endpoint()}/token`, queryString.stringify({
      'client_id': 'PortalCodePkce',
      'grant_type': 'authorization_code',
      'code': token,
      'redirect_uri': configuration.get('portalUrl') + '/sign-in',
      'code_verifier': verifier
    })).then(response => {
      return response.data
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Retrieves the user info using an access token
   *
   * @param {string} accessToken
   *
   * @return {Promise}
   */
  static userInfo (accessToken) {
    return identityApi.get(`${this.endpoint()}/userinfo`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).then(response => {
      return response.data
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Refresh and get authorization token.
   *
   * @param {string} token
   *
   * @return {Promise}
   */
  static refresh (token) {
    var verifier = sessionStorage.getItem('verifier')

    return identityApi.post(`${this.endpoint()}/token`, queryString.stringify({
      'client_id': 'PortalCodePkce',
      'grant_type': 'refresh_token',
      'refresh_token': token,
      'redirect_uri': configuration.get('portalUrl') + '/sign-in',
      'code_verifier': verifier
    })).then(response => {
      return response.data
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Endpoint of API.
   *
   * @return {string}
   */
  static endpoint () {
    return '/connect'
  }
}
